import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 bg-black text-white p-12 flex-col justify-between">
        <div>
          <div className="flex items-center gap-3">
            <img src="/logo-vdv.png" alt="Viaja de Verdad" className="h-12 w-auto"/>
          </div>
        </div>
        <div className="max-w-md">
          <p className="text-[11px] tracking-widest uppercase text-[#20B384] mb-3">Asistente de Reservas</p>
          <h1 className="text-4xl font-semibold tracking-tight leading-tight mb-4">
            Automatiza los correos<br/>de reserva a tus proveedores.
          </h1>
          <p className="text-sm text-gray-300 leading-relaxed">
            Plantillas precargadas con los datos del itinerario. Edita, revisa y envía en segundos —
            hoteles, transfers y actividades, en tres idiomas.
          </p>
        </div>
        <div className="text-[11px] text-gray-500">© {new Date().getFullYear()} Viaja de Verdad</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <h2 className="text-2xl font-semibold tracking-tight">Iniciar sesión</h2>
          <p className="text-sm text-gray-500 mt-1 mb-6">Accede a tu panel de operaciones.</p>

          <div className="rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-[12px] text-blue-800 mb-5">
            <strong>Primera vez aquí?</strong> Si tu administrador te ha invitado, escribe tu email corporativo y la contraseña que quieras usar a partir de ahora. Quedará guardada.
          </div>

          <div className="space-y-4">
            <div>
              <Label className="vv-label">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="eduardo@viajadverdad.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="vv-label">Contraseña</Label>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
                className="mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading && <Loader2 size={16} className="mr-2 animate-spin" />}
            Entrar
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-gray-200"/>
            <span className="text-[11px] uppercase tracking-wider text-gray-400">o</span>
            <div className="flex-1 h-px bg-gray-200"/>
          </div>

          <GoogleSignInButton/>

          <p className="text-xs text-gray-400 mt-6">
            ¿Olvidaste tu contraseña? Pide al administrador que la restablezca.
          </p>
        </form>
      </div>
    </div>
  );
}
